import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ReleaseNotesModal = ({ show, setShow, onDismissForever }) => {
    const { token } = useToken();
    const [loading, setLoading] = useState(false);
    const contentRef = useRef(null);

    const sprints = [
        { id: 35, name: 'Sprint 35', date: '10 Sep 2026', isCurrent: true },
        { id: 34, name: 'Sprint 34', date: '19 Ago 2026', isCurrent: false },
        { id: 33, name: 'Sprint 33', date: '29 Jul 2026', isCurrent: false }
    ];
    const [selectedSprint, setSelectedSprint] = useState(sprints[0]);

    const scrollToSprint = (sprint) => {
        setSelectedSprint(sprint);
        const element = document.getElementById(`sprint-${sprint.id}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    useEffect(() => {
        if (!show) return;

        const observerOptions = {
            root: contentRef.current,
            rootMargin: '-10% 0px -55% 0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const sprintId = parseInt(entry.target.id.replace('sprint-', ''), 10);
                    const sprint = sprints.find(s => s.id === sprintId);
                    if (sprint) {
                        setSelectedSprint(sprint);
                    }
                }
            });
        }, observerOptions);

        sprints.forEach((sprint) => {
            const el = document.getElementById(`sprint-${sprint.id}`);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [show]);

    const handleClose = () => {
        setShow(false);
    };

    const handleDismissForever = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/account/DismissReleaseNotes', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                toast.success('Preferencia guardada. No volveremos a mostrarte esto automáticamente.');
                setShow(false);
                if (onDismissForever) {
                    onDismissForever();
                }
            } else {
                toast.error('Error al guardar preferencia');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error de red al guardar preferencia');
        }
        setLoading(false);
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered scrollable={true} contentClassName="border-0 shadow-lg overflow-hidden" className="release-notes-modal p-0">
            <style>
                {`
                .release-notes-modal .modal-content {
                    border-radius: 0.75rem;
                }
                .hover-bg-200:hover {
                    background-color: #e9ecef;
                }
                `}
            </style>
            <div className="d-flex flex-column flex-md-row" style={{ minHeight: '500px', maxHeight: '88vh' }}>
                {/* Sidebar - Oculto en móviles para evitar bloquear la pantalla y darle prioridad al contenido */}
                <div className="d-none d-md-flex bg-light p-4 flex-column border-end" style={{ width: '100%', maxWidth: '280px' }}>
                    <div className="mb-4 mt-2">
                        <span className="badge bg-primary px-2 py-1 fs--2 mb-3 shadow-sm" style={{ letterSpacing: '0.5px' }}>ADMINPANEL</span>
                        <h2 className="text-primary fw-bold mb-0" style={{ letterSpacing: '-0.5px' }}>Sprints</h2>
                    </div>
                    
                    <div className="flex-grow-1 mt-3">
                        {sprints.map(sprint => (
                            <div 
                                key={sprint.id}
                                onClick={() => scrollToSprint(sprint)}
                                className={`rounded p-3 mb-2 d-flex align-items-center justify-content-between cursor-pointer transition-base ${
                                    selectedSprint.id === sprint.id 
                                    ? 'bg-primary text-white shadow-sm' 
                                    : 'text-600 hover-bg-200'
                                }`}
                            >
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={sprint.isCurrent ? "check-circle" : "history"} className="me-3 fs-1" />
                                    <span className={`fs-0 ${selectedSprint.id === sprint.id ? 'fw-semi-bold' : ''}`}>
                                        {sprint.name}
                                    </span>
                                </div>
                                {sprint.isCurrent && (
                                    <span className="fs--2 fw-bold text-primary bg-white rounded-pill px-2 py-1 shadow-sm">ACTUAL</span>
                                )}
                            </div>
                        ))}
                    </div>
                    
                   
                </div>

                {/* Main Content */}
                <div className="flex-grow-1 d-flex flex-column bg-white position-relative" style={{ minWidth: 0, overflow: 'hidden' }}>
                    {/* Header */}
                    <div className="p-4 px-md-5 border-bottom d-flex justify-content-between align-items-center bg-white sticky-top" style={{ zIndex: 10 }}>
                        <div className="d-flex align-items-center text-primary">
                            <FontAwesomeIcon icon="code-branch" className="me-2 me-md-3 fs-1 fs-md-2" />
                            <h3 className="mb-0 fw-bold fs-1 fs-md-2">Adminpanel Release Notes</h3>
                        </div>
                    </div>

                    {/* Mobile Sprints Horizontal Bar (Cute pills/tabs) */}
                    <div className="d-flex d-md-none align-items-center px-4 py-3 bg-light border-bottom overflow-auto" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', gap: '10px' }}>
                        <span className="text-400 fs--2 fw-bold text-uppercase me-2" style={{ letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Sprints:</span>
                        {sprints.map(sprint => (
                            <button
                                key={sprint.id}
                                type="button"
                                onClick={() => scrollToSprint(sprint)}
                                className={`btn btn-sm rounded-pill d-flex align-items-center px-3 py-2 shadow-none transition-base ${
                                    selectedSprint.id === sprint.id
                                        ? 'btn-primary text-white shadow-sm'
                                        : 'btn-outline-secondary bg-white text-700 border-200'
                                }`}
                                style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}
                            >
                                <FontAwesomeIcon icon={sprint.isCurrent ? "check-circle" : "history"} className="me-2 fs--1" />
                                <span>{sprint.name}</span>
                                {sprint.isCurrent && (
                                    <span className="badge bg-white text-primary ms-2 px-2 py-1 fs--2 rounded-pill shadow-xs">Actual</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Content body */}
                    <div ref={contentRef} className="p-4 px-md-5 flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                        
                        <div className="bg-light p-4 rounded-3 border position-relative">
                            {/* Timeline vertical line */}
                            <div className="position-absolute bg-300 d-none d-sm-block" style={{ top: '32px', bottom: '32px', left: '31px', width: '2px', zIndex: 0 }}></div>

                            {/* SPRINT 35 */}
                            <div id="sprint-35" className="mb-5 position-relative z-index-1">
                                <div className="d-flex align-items-start">
                                    <div className="mt-1 flex-shrink-0 bg-white d-none d-sm-block" style={{ width: '16px', height: '16px', borderRadius: '50%', border: '4px solid #2c7be5', zIndex: 1 }}></div>
                                    <div className="ms-0 ms-sm-4 flex-grow-1">
                                        <h4 className="fw-bold fs-1 mb-1 d-flex flex-wrap align-items-center gap-2">
                                            <span>Sprint 35</span> 
                                            <span className="badge bg-success fs--2 rounded-pill shadow-sm">Versión actual</span>
                                        </h4>
                                        <p className="text-500 fs--1 mb-4">10 Sep 2026</p>
                                        
                                        <ul className="list-unstyled mb-0">
                                            {/* NOVEDADES */}
                                            <li className="d-flex flex-column flex-xl-row align-items-start mb-4 pb-3 border-bottom border-200">
                                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                                    <span className="badge px-3 py-2 fs--2 rounded shadow-sm" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', letterSpacing: '0.5px' }}>NOVEDADES</span>
                                                </div>
                                                <div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Support Engine]</strong>
                                                        <span className="text-700 fs-0">Nuevo módulo en el menú lateral para la atención y resolución de soporte por WhatsApp:</span>
                                                        <ul className="text-700 fs-0 mb-0 ps-3 mt-1">
                                                            <li className="mb-1">Chat interactivo en vivo con clientes para recepción y envío de mensajes en tiempo real (texto, imágenes, audios y documentos).</li>
                                                            <li className="mb-1">Bandeja de tickets organizada por estados (Pendientes, En Progreso y Resueltos) con asignación a agentes y participantes.</li>
                                                            <li>Atajos y respuestas rápidas de texto, panel lateral con información y licencias del cliente, y métricas de soporte en vivo.</li>
                                                        </ul>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Bancos y Conciliación]</strong>
                                                        <ul className="text-700 fs-0 mb-0 ps-3">
                                                            <li className="mb-1">Nuevo módulo de Conciliación Bancaria.</li>
                                                            <li className="mb-1">Nuevo módulo de gestión de cuentas bancarias.</li>
                                                            <li>Vinculación de Factura con Movimiento bancario.</li>
                                                        </ul>
                                                    </div>
                                                    <div>
                                                        <strong className="text-primary d-block mb-1">[Reportes e Indicadores]</strong>
                                                        <ul className="text-700 fs-0 mb-0 ps-3">
                                                            <li className="mb-1">Reporte gráfico Ingresos Netos 12M.</li>
                                                            <li>Consulta de los usuarios vendedores de los últimos 12 meses en el módulo de Sales Indicators.</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </li>

                                            {/* MEJORAS */}
                                            <li className="d-flex flex-column flex-xl-row align-items-start mb-2">
                                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                                    <span className="badge px-3 py-2 fs--2 rounded shadow-sm" style={{ backgroundColor: '#dcfce7', color: '#166534', letterSpacing: '0.5px' }}>MEJORAS</span>
                                                </div>
                                                <div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Facturación y SAT]</strong>
                                                        <span className="text-700 fs-0">Actualización del estatus del CFDI con la información del SAT.</span>
                                                    </div>
                                                    <div>
                                                        <strong className="text-primary d-block mb-1">[Productos y Pólizas]</strong>
                                                        <ul className="text-700 fs-0 mb-0 ps-3">
                                                            <li className="mb-1">Se eliminó el campo "Canal" en la configuración de productos.</li>
                                                            <li>Los productos de tipo póliza ahora se asignan directamente a una licencia.</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* SPRINT 34 */}
                            <div id="sprint-34" className="mb-5 position-relative z-index-1">
                                <div className="d-flex align-items-start">
                                    <div className="mt-1 flex-shrink-0 bg-white" style={{ width: '16px', height: '16px', borderRadius: '50%', border: '4px solid #cbd0d9', zIndex: 1 }}></div>
                                    <div className="ms-4 flex-grow-1">
                                        <h4 className="fw-bold fs-1 mb-1 d-flex align-items-center">
                                            Sprint 34 
                                        </h4>
                                        <p className="text-500 fs--1 mb-4">19 Ago 2026</p>
                                        
                                        <ul className="list-unstyled mb-0">
                                            {/* NOVEDADES */}
                                            <li className="d-flex flex-column flex-xl-row align-items-start mb-4 pb-3 border-bottom border-200">
                                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                                    <span className="badge px-3 py-2 fs--2 rounded shadow-sm" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', letterSpacing: '0.5px' }}>NOVEDADES</span>
                                                </div>
                                                <div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Clientes]</strong>
                                                        <span className="text-700 fs-0">Reestructuración de la vista de detalles de clientes para un acceso rápido clasificado en Info General, Fiscal, Servicios y Desarrollo.</span>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Licencias]</strong>
                                                        <ul className="text-700 fs-0 mb-0 ps-3">
                                                            <li className="mb-1">Distribución de detalles de licencias en tabs, con un buscador ampliado y ordenamiento avanzado de pagos directo desde la base de datos.</li>
                                                            <li className="mb-1">Se agregó la posibilidad de remover módulos directamente desde la vista de detalles de la licencia.</li>
                                                            <li>Nueva sección de Licencias Vencidas para consultar empresas pendientes y aplicar filtros por antigüedad, tipo de licencia y ordenamiento.</li>
                                                        </ul>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Activos Fijos]</strong>
                                                        <span className="text-700 fs-0">Creación de una nueva vista de análisis y gestión. Se agregó el cálculo y campo en base de datos para la "Depreciación Anual" y se integró el componente de gasto vinculado.</span>
                                                    </div>
                                                    <div>
                                                        <strong className="text-primary d-block mb-1">[Support Desk]</strong>
                                                        <span className="text-700 fs-0">Implementación de nuevos filtros por agente (creador/resolutor) en tickets con resaltado visual.</span>
                                                    </div>
                                                </div>
                                            </li>

                                            {/* MEJORAS Y AJUSTES */}
                                            <li className="d-flex flex-column flex-xl-row align-items-start mb-2">
                                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                                    <span className="badge px-3 py-2 fs--2 rounded shadow-sm" style={{ backgroundColor: '#dcfce7', color: '#166534', letterSpacing: '0.5px' }}>MEJORAS</span>
                                                </div>
                                                <div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Suscripciones]</strong>
                                                        <span className="text-700 fs-0">Nuevo diseño limpio (eliminación de scrolls y ajuste de layout en productos), incluyendo la adición de fechas clave (creación y último pago).</span>
                                                    </div>
                                                    <div className="mb-3">
                                                        <strong className="text-primary d-block mb-1">[Catálogo de Productos]</strong>
                                                        <span className="text-700 fs-0">Optimización del buscador con ejecución inmediata (debounceSearch y eventos DDL), mostrando productos "Activos" por defecto.</span>
                                                    </div>
                                                    <div>
                                                        <strong className="text-primary d-block mb-1">[Ajustes Generales]</strong>
                                                        <span className="text-700 fs-0">Limpieza de botones innecesarios en la vista de empleados.</span>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* SPRINT 33 */}
                            <div id="sprint-33" className="mb-5 position-relative z-index-1">
                                <div className="d-flex align-items-start">
                                    <div className="mt-1 flex-shrink-0 bg-white" style={{ width: '16px', height: '16px', borderRadius: '50%', border: '4px solid #cbd0d9', zIndex: 1 }}></div>
                                    <div className="ms-4 flex-grow-1">
                                        <h4 className="fw-bold fs-1 mb-1 d-flex align-items-center">
                                            Sprint 33
                                        </h4>
                                        <p className="text-500 fs--1 mb-4">29 Jul 2026</p>
                                        
                                        <ul className="list-unstyled mb-0">
                            
                            {/* NOVEDADES */}
                            <li className="d-flex flex-column flex-xl-row align-items-start mb-5 pb-4 border-bottom border-200">
                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                    <span className="badge px-3 py-2 fs--2 rounded shadow-sm" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', letterSpacing: '0.5px' }}>NOVEDADES</span>
                                </div>
                                <div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Contabilidad]</strong>
                                        <span className="text-700 fs-0">Frontend completo para el catálogo de cuentas (estructura de árbol) y funcionalidad para eliminar cuentas.</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Facturación]</strong>
                                        <ul className="text-700 fs-0 mb-0 ps-3">
                                            <li className="mb-1">Soporte para facturas con tasa exenta y tasa cero. Documentos relacionados visibles en facturas.</li>
                                            <li className="mb-1">Nueva vista para "Pagos Recibidos" con más detalles, y tarjetas informativas en pagos emitidos.</li>
                                            <li>Nuevo panel de reporte para planes de facturación.</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <strong className="text-primary d-block mb-1">[Nóminas]</strong>
                                        <span className="text-700 fs-0">Funcionalidad para visualizar detalles de nóminas registradas.</span>
                                    </div>
                                </div>
                            </li>

                            {/* MEJORAS */}
                            <li className="d-flex flex-column flex-xl-row align-items-start mb-5 pb-4 border-bottom border-200">
                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                    <span className="badge px-3 py-2 fs--2 rounded shadow-sm" style={{ backgroundColor: '#dcfce7', color: '#166534', letterSpacing: '0.5px' }}>MEJORAS</span>
                                </div>
                                <div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Logs]</strong>
                                        <span className="text-700 fs-0">Se modificó el servicio de logs de Akasia. Ahora en los detalles de licencia hay una nueva tabla donde se muestran los logs en caso de no ver información actual en la tabla anterior.</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Nube]</strong>
                                        <span className="text-700 fs-0">Al crear una licencia nube ya no es necesario seleccionar la base de datos, ahora el sistema lo hace automáticamente.</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Contabilidad]</strong>
                                        <ul className="text-700 fs-0 mb-0 ps-3">
                                            <li className="mb-1">Se estructuró y mejoró la lógica para importar archivos XML.</li>
                                            <li>El mes/periodo seleccionado ahora se guarda globalmente para mantener el contexto en las vistas contables.</li>
                                        </ul>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Nóminas]</strong>
                                        <span className="text-700 fs-0">Se mejoró la interfaz de la lista de nóminas.</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Facturación]</strong>
                                        <span className="text-700 fs-0">Rediseño del formulario de factura y nuevo modal de descarga de PDF.</span>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Desarrollo al sistema]</strong>
                                        <span className="text-700 fs-0">Restructuración de servicios Prodigia, actualización de endpoints (empresas y contabilidad), y archivo .bat para ejecución simultánea de proyectos.</span>
                                    </div>
                                    <div>
                                        <strong className="text-primary d-block mb-1">[Pagos]</strong>
                                        <span className="text-700 fs-0">Rediseño completo de la interfaz con nuevos componentes para una navegación más fluida.</span>
                                    </div>
                                </div>
                            </li>

                            {/* CORRECCIONES */}
                            <li className="d-flex flex-column flex-xl-row align-items-start mb-2">
                                <div className="mb-3 mb-xl-0 me-xl-4 text-xl-end" style={{ minWidth: '140px' }}>
                                    <span className="badge bg-primary px-3 py-2 rounded shadow-sm fs--2" style={{ letterSpacing: '0.5px' }}>CORRECCIONES</span>
                                </div>
                                <div>
                                    <div className="mb-3">
                                        <strong className="text-primary d-block mb-1">[Nóminas]</strong>
                                        <span className="text-700 fs-0">Se corrigieron y habilitaron los enlaces para descargar archivos XML y PDF directamente desde la lista general.</span>
                                    </div>
                                    <div>
                                        <strong className="text-primary d-block mb-1">[Facturación]</strong>
                                        <span className="text-700 fs-0">Se arregló y mejoró la generación del PDF de complementos de pago.</span>
                                    </div>
                                </div>
                            </li>

                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Plantilla para próximos Sprints:
                            <div id="sprint-32" className="mb-5 position-relative z-index-1">
                                <div className="d-flex align-items-start">
                                    <div className="mt-1 flex-shrink-0 bg-white" style={{ width: '16px', height: '16px', borderRadius: '50%', border: '4px solid #cbd0d9', zIndex: 1 }}></div>
                                    <div className="ms-4 flex-grow-1">
                                        <h4 className="fw-bold fs-1 mb-1">Sprint 32</h4>
                                        <p className="text-500 fs--1 mb-4">15 Jun 2026</p>
                                        <p className="text-700 fs-0">Aquí irá el contenido de las notas del Sprint 32...</p>
                                    </div>
                                </div>
                            </div>

                            <div id="sprint-31" className="mb-2 position-relative z-index-1">
                                <div className="d-flex align-items-start">
                                    <div className="mt-1 flex-shrink-0 bg-white" style={{ width: '16px', height: '16px', borderRadius: '50%', border: '4px solid #cbd0d9', zIndex: 1 }}></div>
                                    <div className="ms-4 flex-grow-1">
                                        <h4 className="fw-bold fs-1 mb-1">Sprint 31</h4>
                                        <p className="text-500 fs--1 mb-4">01 May 2026</p>
                                        <p className="text-700 fs-0">Aquí irá el contenido de las notas del Sprint 31...</p>
                                    </div>
                                </div>
                            </div>
                            */}
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="p-3 p-md-4 px-md-5 border-top bg-light d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 mt-auto">
                        <Button variant="outline-secondary" className="px-3 px-md-4 fw-semi-bold order-2 order-sm-1" onClick={handleDismissForever} disabled={loading}>
                            {loading ? 'Guardando...' : 'No volver a mostrar'}
                        </Button>
                        <Button variant="primary" className="px-4 px-md-5 shadow-sm fw-bold order-1 order-sm-2" onClick={handleClose} disabled={loading}>
                            Cerrar
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

ReleaseNotesModal.propTypes = {
    show: PropTypes.bool.isRequired,
    setShow: PropTypes.func.isRequired,
    onDismissForever: PropTypes.func
};

export default ReleaseNotesModal;
